"use client"

import type React from "react"

import { useEffect, useState } from "react"
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from "react-router-dom"
import { AuthProvider, useAuth } from "./contexts/auth-context"
import DashboardLayout from "./layouts/DashboardLayout"
import LoginPage from "./pages/auth/LoginPage"
import RegisterPage from "./pages/auth/RegisterPage"
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage"
import PendingApprovalPage from "./pages/auth/PendingApprovalPage"
import TicketsPage from "./pages/dashboard/TicketsPage"
import NewTicketPage from "./pages/dashboard/NewTicketPage"
import MessagesPage from "./pages/dashboard/MessagesPage"
import NewMessagePage from "./pages/dashboard/NewMessagePage"
import ThreadPage from "./pages/dashboard/ThreadPage"
import AnnouncementsPage from "./pages/dashboard/AnnouncementsPage"
import BlogPage from "./pages/dashboard/BlogPage"
import AvailableRoomsPage from "./pages/dashboard/AvailableRoomsPage"
import MembershipCardPage from "./pages/dashboard/MembershipCardPage"
import DiscountsPage from "./pages/dashboard/DiscountsPage"
import AdminDashboardPage from "./pages/dashboard/admin/AdminDashboardPage"
import AdminTicketsPage from "./pages/dashboard/admin/AdminTicketsPage"
import AdminAnnouncementsPage from "./pages/dashboard/admin/AdminAnnouncementsPage"
import AdminBlogPage from "./pages/dashboard/admin/AdminBlogPage"
import AdminRoomsPage from "./pages/dashboard/admin/AdminRoomsPage"
import AdminDiscountsPage from "./pages/dashboard/admin/AdminDiscountsPage"
import { Toaster } from "./components/ui/toaster"
import { useAppDispatch } from "./redux/hooks"
// Import the timestamp utility
// Add the import for AdminBuildingsPage
import AdminBuildingsPage from "./pages/dashboard/admin/AdminBuildingsPage"
import GuestRegistrationPage from "./pages/dashboard/GuestRegistrationPage"
import AdminGuestsPage from "./pages/dashboard/admin/AdminGuestsPage"
import TicketDetailPage from "./pages/dashboard/TicketDetailPage"
import BlogArticlePage from "./pages/dashboard/BlogArticlePage"
import AccountPage from "./pages/dashboard/AccountPage"
import TicketThreadPage from "./pages/dashboard/TicketThreadPage"
import GuestThreadPage from "./pages/dashboard/GuestThreadPage"
import { auth } from "./firebase/config"
import { Spinner } from "react-bootstrap"

// Add the import for the notification token utility at the top with other imports
import { updateNotificationToken } from "./utils/notification-token"

// Bridge component that exposes React Router navigation to native mobile apps
const NativeNavigationBridge = () => {
  const navigate = useNavigate()
  useEffect(() => {
    window.navigateToRoute = (route: string) => {
      navigate(route)
    }
    return () => {
      delete window.navigateToRoute
    }
  }, [navigate])
  return null
}
import { onAuthStateChanged } from "firebase/auth"

// Protected route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return <div>Loading...</div>
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth/login" />
  }

  return <>{children}</>
}

// Admin route component
const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return <div>Loading...</div>
  }

  if (!user || user.role !== "admin") {
    return <Navigate to="/dashboard" />
  }

  return <>{children}</>
}

function App() {
  const dispatch = useAppDispatch()
  const [loadedAuth, setLoadedAuth] = useState(false)

  // Add this useEffect after the existing useEffect for auth state
  // Inside the App function, after the existing useEffect
  useEffect(() => {
    // Initialize the notification token handler
    if (typeof window !== "undefined") {
      // This makes the function available for the mobile webview to call
      window.updateNotificationToken = updateNotificationToken
    }
  }, [])

  useEffect(() => {
    onAuthStateChanged(auth, (user) => {
      setLoadedAuth(true)
    })

  }, [dispatch]);


  if (!loadedAuth) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <Spinner animation="border" variant="primary" />
      </div>
    )
  }

  return (
    <Router>
      <NativeNavigationBridge />
      <AuthProvider>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Navigate to="/dashboard" />} />
          <Route path="/auth/login" element={<LoginPage />} />
          <Route path="/auth/register" element={<RegisterPage />} />
          <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/auth/pending-approval" element={<PendingApprovalPage />} />

          {/* Protected dashboard routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AnnouncementsPage />} />
            <Route path="tickets" element={<TicketsPage />} />
            <Route path="tickets/new" element={<NewTicketPage />} />
            <Route path="tickets/:ticketId" element={<TicketDetailPage />} />
            <Route path="tickets/:ticketId/thread" element={<TicketThreadPage />} />
            <Route path="messages" element={<MessagesPage />} />
            <Route path="messages/new" element={<NewMessagePage />} />
            <Route path="messages/:threadId" element={<ThreadPage />} />
            <Route path="announcements" element={<AnnouncementsPage />} />
            <Route path="blog" element={<BlogPage />} />
            <Route path="blog/:articleId" element={<BlogArticlePage />} />
            <Route path="available-rooms" element={<AvailableRoomsPage />} />
            <Route path="membership-card" element={<MembershipCardPage />} />
            <Route path="discounts" element={<DiscountsPage />} />
            <Route path="guests" element={<GuestRegistrationPage />} />
            <Route path="guests/:guestId/thread" element={<GuestThreadPage />} />
            <Route path="account" element={<AccountPage />} />
            {/* Admin routes */}
            <Route
              path="admin/users"
              element={
                <AdminRoute>
                  <AdminDashboardPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/tickets"
              element={
                <AdminRoute>
                  <AdminTicketsPage />
                </AdminRoute>
              }
            />
            <Route path="admin/messages" element={<MessagesPage />} />
            <Route
              path="admin/announcements"
              element={
                <AdminRoute>
                  <AdminAnnouncementsPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/blog"
              element={
                <AdminRoute>
                  <AdminBlogPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/rooms"
              element={
                <AdminRoute>
                  <AdminRoomsPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/discounts"
              element={
                <AdminRoute>
                  <AdminDiscountsPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/guests"
              element={
                <AdminRoute>
                  <AdminGuestsPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/guests/:guestId/thread"
              element={
                <AdminRoute>
                  <GuestThreadPage />
                </AdminRoute>
              }
            />
            {/* Add the route for AdminBuildingsPage in the admin routes section */}
            <Route
              path="admin/buildings"
              element={
                <AdminRoute>
                  <AdminBuildingsPage />
                </AdminRoute>
              }
            />
          </Route>
        </Routes>
        <Toaster />
      </AuthProvider>
    </Router>
  )
}

export default App
